// src/hooks/useFirestoreExtensions.js
// ============================================================
// New hooks and helpers for:
//   1. Employee Active/Inactive Status (attendance-aware)
//   2. Holidays (admin-defined, auto-reflects in attendance)
//   3. Shift Management (admin/lead can define & assign shifts)
// ============================================================

import { useState, useEffect } from "react";
import {
  collection,
  doc,
  query,
  where,
  orderBy,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  getDoc,
  setDoc,
  writeBatch,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase/config.js";
import { getTodayStr } from "./useFirestore.js";
import { isPresent, isWorking, isCheckedOut } from "../utils/attendanceStatus.js";

// ────────────────────────────────────────────────────────────
// HOLIDAY HOOKS
// ────────────────────────────────────────────────────────────

/**
 * useHolidays — real-time listener on the `holidays` collection.
 * Returns all holidays, sorted by date ascending.
 */
export function useHolidays() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const q = query(collection(db, "holidays"), orderBy("date", "asc"));
    const unsub = onSnapshot(
      q,
      (snap) => {
        setData(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      (err) => {
        console.error("[useHolidays]", err.code);
        setLoading(false);
      },
    );
    return unsub;
  }, []);

  return { data, loading };
}

/**
 * useHolidaysInMonth — holidays for a specific year-month.
 */
export function useHolidaysInMonth(year, month) {
  const [data, setData] = useState([]);

  useEffect(() => {
    if (!year || !month) {
      setData([]);
      return;
    }
    const mm = String(month).padStart(2, "0");
    const from = `${year}-${mm}-01`;
    const to = `${year}-${mm}-31`;

    const q = query(
      collection(db, "holidays"),
      where("date", ">=", from),
      where("date", "<=", to),
      orderBy("date", "asc"),
    );

    const unsub = onSnapshot(q, (snap) => {
      setData(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    return unsub;
  }, [year, month]);

  return { data };
}

/**
 * isHoliday — pure helper. Given a date string and holiday list,
 * returns the holiday object or null.
 */
export function isHoliday(dateStr, holidays) {
  return holidays.find((h) => h.date === dateStr) || null;
}

// ── Holiday write helpers ──────────────────────────────────

/**
 * createHoliday — add a holiday.
 * @param {{ name, date, type }} holiday
 */
export async function createHoliday({ name, date, type = "public" }) {
  // Validate: date must be strictly after today
  const today = getTodayStr();
  if (!date || date <= today) {
    throw new Error(
      "Holiday date must be at least 1 day ahead. Today and past dates are not allowed.",
    );
  }

  // Prevent duplicate dates
  const existing = await getDocs(
    query(collection(db, "holidays"), where("date", "==", date)),
  );
  if (!existing.empty)
    throw new Error("A holiday already exists on this date.");

  await addDoc(collection(db, "holidays"), {
    name,
    date, // "YYYY-MM-DD"
    type, // "public" | "optional" | "restricted"
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

export async function updateHoliday(holidayId, updates) {
  await updateDoc(doc(db, "holidays", holidayId), {
    ...updates,
    updatedAt: serverTimestamp(),
  });
}

export async function deleteHoliday(holidayId) {
  await deleteDoc(doc(db, "holidays", holidayId));
}

/**
 * useUpcomingHolidays — real-time listener returning only future holidays
 * (strictly after today), sorted by date ascending.
 */
export function useUpcomingHolidays() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const today = getTodayStr();
    const q = query(
      collection(db, "holidays"),
      where("date", ">", today),
      orderBy("date", "asc"),
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        setData(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      (err) => {
        console.error("[useUpcomingHolidays]", err.code);
        setLoading(false);
      },
    );
    return unsub;
  }, []);

  return { data, loading };
}

// ────────────────────────────────────────────────────────────
// SHIFT HOOKS
// ────────────────────────────────────────────────────────────

/**
 * useShifts — real-time listener on the `shifts` collection.
 */
export function useShifts() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const q = collection(db, "shifts");
    const unsub = onSnapshot(
      q,
      (snap) => {
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        // Sort by sortOrder, then by name
        list.sort((a, b) => {
          const o1 = a.sortOrder !== undefined ? a.sortOrder : 999;
          const o2 = b.sortOrder !== undefined ? b.sortOrder : 999;
          if (o1 !== o2) return o1 - o2;
          return (a.name || "").localeCompare(b.name || "");
        });
        setData(list);
        setLoading(false);
      },
      (err) => {
        console.error("[useShifts]", err.code);
        setLoading(false);
      },
    );
    return unsub;
  }, []);

  return { data, loading };
}

/**
 * useEmployeeShift — get the current APPROVED shift assigned to a specific employee.
 */
export function useEmployeeShift(userId) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) {
      setData(null);
      setLoading(false);
      return;
    }

    // We look for an APPROVED shift assignment for this user
    const q = query(
      collection(db, "shiftAssignments"),
      where("userId", "==", userId),
      where("status", "in", ["APPROVED", "Approved", "approved"]),
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        if (!snap.empty) {
          const doc = snap.docs[0];
          setData({ id: doc.id, ...doc.data() });
        } else {
          setData(null);
        }
        setLoading(false);
      },
      (err) => {
        console.error("[useEmployeeShift]", err.code);
        setLoading(false);
      },
    );
    return unsub;
  }, [userId]);

  return { data, loading };
}

/**
 * useAllShiftAssignments — all assignments (admin view).
 */
export function useAllShiftAssignments() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, "shiftAssignments"),
      (snap) => {
        setData(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      (err) => {
        console.error("[useAllShiftAssignments]", err.code);
        setLoading(false);
      },
    );
    return unsub;
  }, []);

  return { data, loading };
}

/**
 * usePendingShiftRequests — listener for PENDING_ADMIN requests.
 */
export function usePendingShiftRequests() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const q = query(
      collection(db, "shiftAssignments"),
      where("status", "in", ["PENDING_ADMIN", "Pending Admin", "PENDING ADMIN", "pending_admin"]),
    );

    const unsub = onSnapshot(q, (snap) => {
      const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      docs.sort((a, b) => {
        const t1 = a.createdAt?.toMillis?.() ?? 0;
        const t2 = b.createdAt?.toMillis?.() ?? 0;
        return t2 - t1; // desc
      });
      setData(docs);
      setLoading(false);
    }, (err) => {
      console.error("[usePendingShiftRequests]", err);
      setLoading(false);
    });
    return unsub;
  }, []);

  return { data, loading };
}

// ── Shift write helpers ────────────────────────────────────

/**
 * createShift — define a new shift.
 * @param {{ name, startTime, endTime, type, description, createdBy }} shift
 * type: "morning" | "evening" | "night" | "custom"
 */
export async function createShift({
  name,
  startTime,
  endTime,
  type = "custom",
  description = "",
  createdBy,
}) {
  // Duplicate timing check (same startTime + endTime)
  const existingSnap = await getDocs(collection(db, "shifts"));
  const duplicate = existingSnap.docs.some(
    (d) => d.data().startTime === startTime && d.data().endTime === endTime,
  );
  if (duplicate) {
    throw new Error("Shift already exists for selected timings.");
  }

  await addDoc(collection(db, "shifts"), {
    name,
    startTime, // "HH:MM" 24h
    endTime, // "HH:MM" 24h
    type,
    description,
    createdBy,
    isActive: true,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

export async function updateShift(shiftId, updates) {
  await updateDoc(doc(db, "shifts", shiftId), {
    ...updates,
    updatedAt: serverTimestamp(),
  });
}

export async function deleteShift(shiftId) {
  await deleteDoc(doc(db, "shifts", shiftId));
}

// ── Standard Shifts ─────────────────────────────────────────
const STANDARD_SHIFTS = [
  {
    id: "general-shift",
    name: "General Shift",
    startTime: "09:00",
    endTime: "18:00",
    type: "GENERAL",
    sortOrder: 2,
  },
  {
    id: "morning-shift",
    name: "Morning Shift",
    startTime: "06:00",
    endTime: "15:00",
    type: "MORNING",
    sortOrder: 1,
  },
  {
    id: "evening-shift",
    name: "Evening Shift",
    startTime: "14:00",
    endTime: "23:00",
    type: "EVENING",
    sortOrder: 3,
  },
  {
    id: "night-shift",
    name: "Night Shift",
    startTime: "22:00",
    endTime: "07:00",
    type: "NIGHT",
    sortOrder: 4,
  },
];

const STANDARD_IDS = new Set(STANDARD_SHIFTS.map((s) => s.id));

/**
 * seedStandardShifts — called once on Admin login.
 * Deletes any non-standard shifts and upserts the 4 canonical shifts.
 * Safe to call multiple times (idempotent).
 */
export async function seedStandardShifts(createdBy) {
  const snap = await getDocs(collection(db, "shifts"));
  const batch = writeBatch(db);

  // Delete shifts that are NOT one of the 4 standard IDs
  snap.docs.forEach((d) => {
    if (!STANDARD_IDS.has(d.id)) {
      batch.delete(doc(db, "shifts", d.id));
    }
  });

  // Upsert the 4 standard shifts with fixed document IDs
  STANDARD_SHIFTS.forEach(({ id, ...data }) => {
    batch.set(
      doc(db, "shifts", id),
      {
        ...data,
        isActive: true,
        createdBy: createdBy || "system",
        updatedAt: serverTimestamp(),
      },
      { merge: true },
    );
  });

  await batch.commit();

  // Correct any spelling mistakes of "General Shift" in shiftAssignments
  try {
    const assignmentsSnap = await getDocs(collection(db, "shiftAssignments"));
    const assignmentBatch = writeBatch(db);
    let hasAssignmentUpdates = false;

    assignmentsSnap.docs.forEach((d) => {
      const data = d.data();
      const sName = data.shiftName || "";
      const sNameLower = sName.toLowerCase();
      const sStart = data.shiftStart || "";
      const sEnd = data.shiftEnd || "";
      
      const isMisspelledGeneral = sNameLower.startsWith("gen") && !sNameLower.startsWith("general");
      const isGeneralHoursButWrongName = (sStart === "09:00" && sEnd === "18:00" && sName !== "General Shift" && sName !== "Admin Working Hours");

      if (isMisspelledGeneral || isGeneralHoursButWrongName) {
        assignmentBatch.update(doc(db, "shiftAssignments", d.id), {
          shiftName: "General Shift",
          updatedAt: serverTimestamp(),
        });
        hasAssignmentUpdates = true;
      }
    });

    if (hasAssignmentUpdates) {
      await assignmentBatch.commit();
      console.log("Successfully corrected shift names in shiftAssignments.");
    }
  } catch (err) {
    console.warn("Failed to correct shift assignments in migration:", err);
  }
}

const cleanShiftNameForDb = (name, start, end) => {
  if (!name) return "";
  const lower = name.toLowerCase();
  if (lower.startsWith("gen") && !lower.startsWith("general")) {
    return "General Shift";
  }
  if (start && end) {
    const sClean = start.replace(/\s*[AP]M\s*/gi, "").trim();
    const eClean = end.replace(/\s*[AP]M\s*/gi, "").trim();
    if (
      (sClean === "09:00" || sClean === "9:00") &&
      (eClean === "18:00" || eClean === "6:00" || eClean === "06:00") &&
      name !== "Admin Working Hours"
    ) {
      return "General Shift";
    }
  }
  return name;
};

/**
 * assignShift — assign a shift to an employee.
 * status: PENDING_ADMIN | APPROVED | REJECTED
 */
export async function assignShift({
  userId,
  employeeId,
  employeeName,
  shiftId,
  shiftName,
  shiftStart,
  shiftEnd,
  assignedBy,
  assignedByRole,
  teamLeadId,
  status = "PENDING_ADMIN",
  approvedBy = null,
  reviewedBy = null,
}) {
  const batch = writeBatch(db);
  const newAssignmentRef = doc(collection(db, "shiftAssignments"));

  const payload = {
    userId: userId || "",
    employeeId: employeeId || "",
    employeeName: employeeName || "",
    shiftId: shiftId || "",
    shiftName: cleanShiftNameForDb(shiftName || "", shiftStart, shiftEnd),
    shiftStart: shiftStart || "",
    shiftEnd: shiftEnd || "",
    assignedBy: assignedBy || "",
    assignedByRole: assignedByRole || "",
    teamLeadId: teamLeadId || null,
    status: status || "PENDING_ADMIN",
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    assignedAt: serverTimestamp(),
    approvedBy: approvedBy || (status === "APPROVED" ? assignedBy : ""),
    reviewedBy: reviewedBy || (status === "APPROVED" ? assignedBy : ""),
    reviewedAt: status === "APPROVED" ? serverTimestamp() : null,
  };

  // If approved, atomically delete all other assignments for this user
  if (status?.toUpperCase() === "APPROVED") {
    const assignmentsQuery = query(
      collection(db, "shiftAssignments"),
      where("userId", "==", userId || "")
    );
    const assignmentsSnap = await getDocs(assignmentsQuery);
    assignmentsSnap.forEach((d) => {
      batch.delete(d.ref);
    });
  } else if (status?.toUpperCase() === "PENDING_ADMIN" || status?.toUpperCase() === "PENDING ADMIN") {
    const existingQuery = query(
      collection(db, "shiftAssignments"),
      where("userId", "==", userId || ""),
      where("status", "in", ["APPROVED", "Approved", "approved", "PENDING_ADMIN", "Pending Admin", "PENDING ADMIN", "pending_admin"])
    );
    const existingSnap = await getDocs(existingQuery);
    if (!existingSnap.empty) {
      throw new Error("Employee already has an active or pending shift assignment.");
    }
  }

  batch.set(newAssignmentRef, payload);
  await batch.commit();
}

/**
 * reviewShiftAssignment — Admin action to approve/reject.
 */
export async function reviewShiftAssignment(assignmentId, status, reviewedBy, approvedBy = null) {
  const ref = doc(db, "shiftAssignments", assignmentId);
  const requestSnap = await getDoc(ref);
  if (!requestSnap.exists()) {
    throw new Error("Request document not found.");
  }
  const requestData = requestSnap.data();
  const employeeUid = requestData.userId || "";

  const batch = writeBatch(db);

  if (status === "APPROVED") {
    // 1. Update target request to APPROVED and fill structure completely
    batch.update(ref, {
      employeeId: requestData.employeeId || "",
      shiftId: requestData.shiftId || "",
      shiftName: requestData.shiftName || "",
      assignedBy: requestData.assignedBy || "",
      status: "APPROVED",
      reviewedBy: reviewedBy || "Admin",
      approvedBy: approvedBy || reviewedBy || "Admin",
      reviewedAt: serverTimestamp(),
      assignedAt: requestData.createdAt || serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    // 2. Delete all other assignments for this user
    const assignmentsQuery = query(
      collection(db, "shiftAssignments"),
      where("userId", "==", employeeUid)
    );
    const assignmentsSnap = await getDocs(assignmentsQuery);
    assignmentsSnap.forEach((d) => {
      if (d.id !== assignmentId) {
        batch.delete(d.ref);
      }
    });
  } else {
    // Just update the status and review details
    batch.update(ref, {
      employeeId: requestData.employeeId || "",
      shiftId: requestData.shiftId || "",
      shiftName: requestData.shiftName || "",
      assignedBy: requestData.assignedBy || "",
      status: status || "REJECTED",
      reviewedBy: reviewedBy || "Admin",
      approvedBy: approvedBy || reviewedBy || "Admin",
      reviewedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  }

  await batch.commit();
}

export async function removeShiftAssignment(assignmentId) {
  await deleteDoc(doc(db, "shiftAssignments", assignmentId));
}

// ────────────────────────────────────────────────────────────
// EMPLOYEE STATUS LOGIC
// ────────────────────────────────────────────────────────────

/**
 * resolveEmployeeStatus — pure function.
 * Given an employee, their attendance record for today, approved leave
 * requests, and today's holiday, returns:
 *   "active"   — checked in / working
 *   "on_leave" — approved leave covers today
 *   "holiday"  — today is a public holiday
 *   "absent"   — none of the above, should have been working
 *   "weekend"  — today is Sat/Sun
 *
 * @param {object} employee   - user doc
 * @param {object|null} att   - today's attendance doc or null
 * @param {object[]} leaves   - all approved leave requests for this employee
 * @param {object|null} holiday - today's holiday doc or null
 * @param {string} todayStr   - "YYYY-MM-DD"
 * @returns {{ status, label, color }}
 */
export function resolveEmployeeStatus(
  employee,
  att,
  leaves,
  holiday,
  todayStr,
) {
  // 1. Weekend check
  const dayOfWeek = new Date(todayStr + "T00:00:00").getDay(); // 0=Sun,6=Sat
  if (dayOfWeek === 0 || dayOfWeek === 6) {
    return { status: "weekend", label: "Weekend", color: "gray" };
  }

  // 2. Holiday check
  if (holiday) {
    return { status: "holiday", label: "Holiday", color: "purple" };
  }

  // 3. Attendance check (Priority: Attendance marked -> Count as Present)
  if (isPresent(att)) {
    if (isWorking(att)) {
      // Check if currently on a break
      const activeBreak = (att.breaks || []).find((b) => !b.endTime);
      if (activeBreak) {
        const breakLabel =
          activeBreak.breakType === "lunch"
            ? "On Lunch Break"
            : activeBreak.breakType === "tea1"
              ? "On Tea Break 1"
              : "On Tea Break 2";
        return { status: "on_break", label: breakLabel, color: "amber" };
      }
      return { status: "active", label: "Working", color: "green" };
    }

    if (isCheckedOut(att)) {
      if (att.status === "AUTO-CHECKED-OUT" || att.status === "auto-checked-out") {
        return { status: "auto-checked-out", label: "Auto Checked-Out", color: "blue" };
      }
      return { status: "completed", label: "Checked-Out", color: "blue" };
    }
  }

  // 4. Leave request checks (Priority: Approved -> Pending -> Rejected/Cancelled)
  const userLeaves = leaves.filter(
    (l) => l.fromDate <= todayStr && l.toDate >= todayStr
  );

  const onLeave = userLeaves.find((l) => l.status === "approved");
  if (onLeave) {
    return { status: "on_leave", label: "On Leave", color: "amber" };
  }

  const pendingLeave = userLeaves.find(
    (l) => l.status === "pending_lead" || l.status === "pending_admin"
  );
  if (pendingLeave) {
    return { status: "pending_leave", label: "Pending Leave", color: "amber" };
  }

  const rejectedLeave = userLeaves.find(
    (l) => l.status === "rejected" || l.status === "cancelled"
  );
  if (rejectedLeave) {
    return { status: "rejected_leave", label: "Rejected/Cancelled Leave", color: "gray" };
  }

  // 5. Default → absent
  return { status: "absent", label: "Absent", color: "red" };
}

/**
 * useEmployeeStatuses — computes status for all employees for today.
 * Returns a map: userId → { status, label, color }
 *
 * @param {object[]} employees  - all employee user docs
 * @param {object[]} todayAtt   - all attendance records for today
 * @param {object[]} allLeaves  - all approved leave requests
 * @param {object[]} holidays   - all holidays
 */
export function useEmployeeStatuses(employees, todayAtt, allLeaves, holidays) {
  const todayStr = getTodayStr();
  const todayHoliday = isHoliday(todayStr, holidays);

  const statusMap = {};

  for (const emp of employees) {
    const att = todayAtt.find((a) => a.userId === emp.uid) || null;
    const empLeaves = allLeaves.filter((l) => l.userId === emp.uid);
    statusMap[emp.uid] = resolveEmployeeStatus(
      emp,
      att,
      empLeaves,
      todayHoliday,
      todayStr,
    );
  }

  return statusMap;
}

// ────────────────────────────────────────────────────────────
// TEAM REMOVAL REQUESTS HOOKS
// Workflow: Team Lead requests removal → Admin approves/rejects
// ────────────────────────────────────────────────────────────

/**
 * usePendingRemovalRequests — real-time listener for all PENDING_ADMIN removal requests.
 * Admin sees these on the Admin Employees page.
 */
export function usePendingRemovalRequests() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Simple query: only filter by status to avoid index requirement
    const q = query(
      collection(db, "teamRemovalRequests"),
      where("status", "==", "PENDING_ADMIN"),
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        // Sort client-side to avoid Firestore index errors
        const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        docs.sort(
          (a, b) =>
            (b.requestedAt?.toMillis?.() ?? 0) -
            (a.requestedAt?.toMillis?.() ?? 0),
        );
        setData(docs);
        setLoading(false);
      },
      (err) => {
        console.error("[usePendingRemovalRequests]", err.code, err.message);
        setLoading(false);
      },
    );
    return unsub;
  }, []);

  return { data, loading };
}

/**
 * createRemovalRequest — Team Lead submits a removal request.
 * @param {{employeeId, employeeUid, employeeName, teamLeadId, teamLeadName, reason}} params
 */
export async function createRemovalRequest({
  employeeId,
  employeeUid,
  employeeName,
  teamLeadId,
  teamLeadName,
  reason = "",
}) {
  await addDoc(collection(db, "teamRemovalRequests"), {
    employeeId,
    employeeUid,
    employeeName,
    teamLeadId,
    teamLeadName,
    reason,
    requestedAt: serverTimestamp(),
    status: "PENDING_ADMIN",
    approvedBy: null,
    approvedAt: null,
    rejectionReason: null,
    updatedAt: serverTimestamp(),
  });
}

/**
 * approveRemovalRequest — Admin approves removal and executes the removal process.
 * This performs the actual employee removal (same as the old direct deletion):
 *   1. Unassign user from team
 *   2. Cancel pending leave requests
 *   3. Unassign open tasks
 * @param {string} requestId - removal request document ID
 * @param {string} adminUid - approving admin's UID
 */
export async function approveRemovalRequest(requestId, adminUid) {
  // Get the request document directly by ID
  const requestRef = doc(db, "teamRemovalRequests", requestId);
  const requestSnap = await getDoc(requestRef);

  if (!requestSnap.exists()) throw new Error("Removal request not found");

  const request = { id: requestSnap.id, ...requestSnap.data() };
  const employeeUid = request.employeeUid;

  // Execute the removal (batch operation - same as before)
  const batch = writeBatch(db);

  // 1. Unassign user from team
  batch.update(doc(db, "users", employeeUid), {
    teamId: null,
    teamLeadId: null,
    managerId: null,
    updatedAt: serverTimestamp(),
  });

  // 2. Cancel their pending leave requests
  const leaveSnap = await getDocs(
    query(
      collection(db, "leaveRequests"),
      where("userId", "==", employeeUid),
      where("status", "in", ["pending_lead", "pending_admin"]),
    ),
  );
  leaveSnap.forEach((d) => {
    batch.update(d.ref, {
      status: "rejected",
      rejectedBy: adminUid,
      rejectedAt: serverTimestamp(),
      rejectedReason: "Employee removed from team by admin",
      updatedAt: serverTimestamp(),
    });
  });

  // 3. Unassign open tasks
  const taskSnap = await getDocs(
    query(
      collection(db, "tasks"),
      where("assignedTo", "==", employeeUid),
      where("status", "in", ["pending", "in_progress", "PENDING", "IN_PROGRESS"]),
    ),
  );
  taskSnap.forEach((d) => {
    batch.update(d.ref, {
      assignedTo: null,
      assignedToName: `[Removed] ${request.employeeName}`,
      updatedAt: serverTimestamp(),
    });
  });

  // 4. Mark the removal request as APPROVED
  batch.update(doc(db, "teamRemovalRequests", requestId), {
    status: "APPROVED",
    approvedBy: adminUid,
    approvedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  await batch.commit();
}

/**
 * rejectRemovalRequest — Admin rejects the removal request.
 * @param {string} requestId
 * @param {string} adminUid - rejecting admin's UID
 * @param {string} rejectionReason - why it was rejected
 */
export async function rejectRemovalRequest(
  requestId,
  adminUid,
  rejectionReason = "",
) {
  await updateDoc(doc(db, "teamRemovalRequests", requestId), {
    status: "REJECTED",
    approvedBy: adminUid,
    approvedAt: serverTimestamp(),
    rejectionReason,
    updatedAt: serverTimestamp(),
  });
}

export function useExtraBreaks(teamId = null) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let q = collection(db, "extraBreaks");
    if (teamId) {
      q = query(q, where("teamId", "==", teamId));
    }
    const unsub = onSnapshot(
      q,
      (snap) => {
        const results = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        // Sort descending by createdAt
        results.sort((a, b) => {
          const t1 = a.createdAt?.toMillis() || 0;
          const t2 = b.createdAt?.toMillis() || 0;
          return t2 - t1;
        });
        setData(results);
        setLoading(false);
      },
      (err) => {
        console.error("[useExtraBreaks]", err);
        setLoading(false);
      }
    );
    return unsub;
  }, [teamId]);

  return { data, loading };
}
